import React from "react";
import {
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Card,
  Button,
  Autocomplete,
  InputAdornment,
  Divider,
  Chip,
  alpha,
  Stack,
  Tooltip,
} from "@mui/material";
import { FormikProvider } from "formik";
import type { FormikProps } from "formik";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { styled, useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PremiumTimeInput from "../../../Components/PremiumTimeInput";
import { IoSearchSharp } from "react-icons/io5";
import {
  MdPerson,
  MdLocationOn,
  MdSchedule,
  MdSubject,
  MdCalendarToday,
  MdLaptop,
  MdGroups,
  MdNotes,
} from "react-icons/md";
import type {
  CustomerOption,
  IGrade,
  ISubjectGradePerent,
} from "../../../Api/Interface/api.interface";
import { ClassType, TeachMood, WeekDays } from "../../../Core/Enum/enum";
import { normalizeWeekday as _normalizeWeekday } from "../weekdayHelpers";
import { MdAdd } from "react-icons/md";
import AddTeachingSubjectDialog from "./AddTeachingSubjectDialog";
import ClassBillingSection from "./ClassBillingSection";
import { previewCompactLabel } from "../../../utils/classLabel";

/**
 * Shared form for Create Class + Edit Class.
 *
 * Extracted from the (formerly monolithic) Create page so both routes render
 * from one JSX tree. The Edit page's own safety rules (protected subjects,
 * capacity floor, hybrid narrowing, edit-scope band) plug in through the
 * props declared below — the component itself carries no mode-specific
 * behaviour, only mode-specific *display* (button copy, cancel visibility).
 *
 * Formik state, validation and submit stay in the parent page. This
 * component only renders and reports events; it never mutates state
 * directly except through the callbacks passed in.
 */

// ── Styled components (visual language of Create Class, now shared) ──

export const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  overflow: "visible",
  transition: "box-shadow 0.2s ease",
  "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2.5, 3, 0, 3),
}));

export const SectionBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3, 3),
}));

export const ModeButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  borderRadius: 12,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
  transition: "all 0.2s ease",
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.08)
    : "transparent",
  color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

export const DayChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 48,
  height: 48,
  borderRadius: 12,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
  transition: "all 0.2s ease",
  backgroundColor: selected
    ? theme.palette.primary.main
    : alpha(theme.palette.primary.main, 0.06),
  color: selected ? "#fff" : theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.12),
  },
}));

export const SubjectChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "locked",
})<{ selected?: boolean; locked?: boolean }>(({ theme, selected, locked }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: theme.spacing(0.75, 2),
  borderRadius: 20,
  cursor: locked ? "not-allowed" : "pointer",
  fontWeight: 500,
  fontSize: 13,
  transition: "all 0.2s ease",
  backgroundColor: selected
    ? theme.palette.primary.main
    : alpha(theme.palette.primary.main, 0.06),
  color: selected ? "#fff" : theme.palette.text.primary,
  opacity: locked ? 0.85 : 1,
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.12),
  },
}));

export const GradeBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 80,
  padding: theme.spacing(1, 2),
  borderRadius: 12,
  fontWeight: 700,
  fontSize: 14,
  backgroundColor: "#54b8e8",
  color: "#fff",
}));

// ── Helpers (also exported for the pages) ──

export const studyMoodLabel = (unit: string) => {
  switch (unit) {
    case TeachMood.Online:
      return "Online";
    case TeachMood.Offline:
      return "In Person";
    case TeachMood.Hybrid:
      return "Online + In-Person";
    default:
      return "";
  }
};

export const requiresLocation = (mode: string) =>
  mode === TeachMood.Offline || mode === TeachMood.Hybrid;

/**
 * Normalise any class_mode string the backend may hand back (lowercase
 * canonical "online"/"in_person"/"hybrid", legacy capitalised values,
 * aliases like "virtual", "InPerson", "blended") into the exact UI
 * token `TeachMood.*`. Formik's class_mode value, togglesFromMode,
 * requiresLocation, and the edit-page dirty snapshot all compare
 * strings exactly, so a mismatch means (a) neither toggle button
 * appears selected on load and (b) hasChanges never flips when the
 * user clicks — because the "new" value and the "server" value only
 * differ in case/alias. See the 2026-10-08 Edit Class audit for the
 * full chain. Keep this list in lockstep with App\Support\StudyMode
 * on the backend (ONLINE_ALIASES / IN_PERSON_ALIASES / HYBRID_ALIASES).
 */
export const normalizeTeachMode = (raw: unknown): string => {
  if (raw === null || raw === undefined) return "";
  const m = String(raw).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (m === "") return "";
  if (["online", "virtual", "remote"].includes(m)) return TeachMood.Online;
  if (["offline", "inperson", "in_person", "physical", "onsite"].includes(m)) return TeachMood.Offline;
  if (["hybrid", "blended"].includes(m)) return TeachMood.Hybrid;
  // Unknown value — surface the trimmed original rather than coercing to
  // empty so an admin can still see "something is off" in the toggle row.
  return String(raw).trim();
};

// Weekday canonicalisation lives in a React-free helper module so tests
// don't pull MUI / RTK Query. Re-exported here to preserve the import
// surface callers already use (`import { normalizeWeekday } from "../ClassForm"`).
export { normalizeWeekday } from "../weekdayHelpers";

export const modeFromToggles = (online: boolean, inPerson: boolean): string => {
  if (online && inPerson) return TeachMood.Hybrid;
  if (online) return TeachMood.Online;
  if (inPerson) return TeachMood.Offline;
  return "";
};

export const togglesFromMode = (mode: string) => ({
  online: mode === TeachMood.Online || mode === TeachMood.Hybrid,
  inPerson: mode === TeachMood.Offline || mode === TeachMood.Hybrid,
});

// ── Props ──

export type ClassFormMode = "create" | "edit";

export type TimeConflict = { day: string; startTime: string; endTime: string; classId: number };

export interface ClassFormProps {
  mode: ClassFormMode;

  // Formik lives in the parent so validation and submit are page-specific.
  formik: FormikProps<any>;
  formValid: boolean;
  submitting: boolean;
  // Edit page only: when the parent tracks dirty state itself (Formik `dirty`
  // is insufficient because subjects/weekdays/programType live outside the
  // form), it passes `hasChanges=false` to keep Save Changes disabled until
  // the user actually edits something. Create leaves it undefined so the
  // button gates on validity alone.
  hasChanges?: boolean;
  // Edit page only: human-readable reason the form is currently invalid, so
  // Save buttons can show a tooltip instead of silently disabling (brief §7).
  // Null when the form is valid or when the parent doesn't track it.
  validationReason?: string | null;

  // Header — mode drives copy but the page can override for edit-specific
  // context (class name in the subtitle, "Editing ‘Grade 4 Math’"…).
  heading?: string;
  subheading?: string;

  // Staff lists — filteredStaffOptions is what the Autocomplete renders,
  // studentPerentOptions is the full set used for value resolution.
  studentPerentOptions: CustomerOption[];
  filteredStaffOptions: CustomerOption[];
  studentPerentIsLoading: boolean;
  onStaffChange: (event: any, value: CustomerOption | null) => void;
  onStaffSearchInput: (value: string) => void;

  // Location lists.
  locationOptions: CustomerOption[];
  filteredLocationOptions: CustomerOption[];
  staffLocationIds: number[];
  isLocationInvalid: boolean;
  locationIsLoading: boolean;

  // Class mode + class type handlers (Edit will wrap onClassModeChange with
  // the hybrid-narrowing dialog).
  onClassModeChange: (nextMode: string) => void | Promise<void>;
  onClassTypeChange: (nextType: string) => void;

  // Program type is local state on Create and formik-driven on Edit — both
  // pages own it and pass in the current value + setter.
  programType: "regular" | "special";
  onProgramTypeChange: (t: "regular" | "special") => void;

  // Subjects & grades panel.
  subjectGRadeDataList: IGrade[];
  subjectMap: Map<number, string>;
  subjectIsLoading: boolean;
  subjectGradeIsLoading: boolean;
  isSubjectError: boolean;
  selectedSubject: number[];
  onSubjectToggle: (sgId: number) => void;

  // Edit-specific: subjects that have generated classes / enrolled students
  // / attendance / billing and cannot be removed. Empty on Create.
  protectedSubjectIds?: Set<number>;
  protectedSubjectMessage?: string;

  // "Create New Subject" — only shown when the parent supplies a handler
  // "Add Teaching Subject" (matches the Staff Profile workflow) — the
  // dialog is owned by ClassForm; the parent just supplies the callback
  // that appends the newly-selected grade+subject pair-ids into its
  // selectedSubject state. Global subject creation was retired from this
  // form: existing subject creation stays where it belongs (subject
  // management), and the class picker now composes only from existing
  // grade+subject rows the assigned staff is qualified to teach.
  onAddSubjectAssignments?: (newlyAddedGradeSubjectIds: number[]) => void;

  // Days.
  selectedWeekDays: string[];
  onWeekDayToggle: (day: string) => void;

  // Conflict + validity signals surfaced in Days / Time UI.
  timeConflicts: TimeConflict[];
  hasConflicts: boolean;
  timeOrderError: string;

  // Edit-only: capacity floor + assigned count. Both pages accept and both
  // are enforced by the parent's validation; the form only renders the hint.
  assignedCount?: number;

  // Edit-scope band, only rendered when the parent chooses to show it.
  editScope?: "this" | "future" | "series";
  onEditScopeChange?: (s: "this" | "future" | "series") => void;
  showEditScopeBand?: boolean;

  // Optional context band rendered directly below the header — used by the
  // Edit page to show "Editing recurring class schedule / Selected
  // occurrence: {date} / Changes affect: Future occurrences from {date}".
  contextBand?: React.ReactNode;

  // Footer.
  submitLabel: string;
  onReset: () => void;
  onCancel?: () => void;
  showCancel?: boolean;

  // When true, the Staff Member Autocomplete is rendered read-only —
  // used by the Staff Portal Create Class flow where the acting staff
  // identity is derived server-side from Auth and MUST NOT be picked.
  // The field still renders in the same slot with the same visual
  // shape so the two pages read as one form; only interaction is
  // suppressed. Admin passes undefined → falsy → picker stays live.
  staffLocked?: boolean;

  // Billing v2 (Phase B): value on the server row so the "current fee on
  // file" hint + "future invoices only" alert can render on Edit. Null on
  // Create.
  currentClassFeeFromServer?: number | null;
}

const CapsLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      color: "text.secondary",
      mb: 0.75,
      display: "block",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}
  >
    {children}
    {required && <Box component="span" sx={{ color: "#ef4444" }}>*</Box>}
  </Typography>
);

const ClassForm: React.FC<ClassFormProps> = (props) => {
  // Local dialog state — the parent doesn't need to know the picker
  // exists, only that new grade+subject pairs may be appended via the
  // onAddSubjectAssignments callback.
  const [addSubjectOpen, setAddSubjectOpen] = React.useState(false);
  // Parent-owned open state for the Start / End time pickers. Only one
  // can be open at once — opening the other implicitly closes the first.
  const [activeTimePicker, setActiveTimePicker] = React.useState<"start" | "end" | null>(null);
  const {
    mode,
    formik,
    formValid,
    submitting,
    hasChanges,
    heading,
    subheading,
    studentPerentOptions,
    filteredStaffOptions,
    studentPerentIsLoading,
    onStaffChange,
    onStaffSearchInput,
    locationOptions,
    filteredLocationOptions,
    staffLocationIds,
    isLocationInvalid,
    locationIsLoading,
    onClassModeChange,
    onClassTypeChange,
    programType,
    onProgramTypeChange,
    subjectGRadeDataList,
    subjectMap,
    subjectIsLoading,
    subjectGradeIsLoading,
    isSubjectError,
    selectedSubject,
    onSubjectToggle,
    protectedSubjectIds,
    protectedSubjectMessage,
    onAddSubjectAssignments,
    selectedWeekDays,
    onWeekDayToggle,
    timeConflicts,
    hasConflicts,
    timeOrderError,
    assignedCount,
    editScope,
    onEditScopeChange,
    showEditScopeBand,
    submitLabel,
    onReset,
    onCancel,
    showCancel,
    contextBand,
    staffLocked,
    validationReason,
  } = props;

  const theme = useTheme();
  const defaultHeading = mode === "edit" ? "Edit Class" : "Create New Class";
  const defaultSubheading = mode === "edit"
    ? "Update staff, teaching mode, subjects, capacity, and future schedule details."
    : "Set up a new class by selecting a staff member, schedule, and subjects.";

  // One source of truth for the Save button's enabled state and for the
  // tooltip we show when it's disabled. Both the top-sticky Save and the
  // bottom Save use these exact values so there is no path where the two
  // disagree. On Create, hasChanges is undefined → the dirty-gate is skipped;
  // on Edit it is required to flip to true before Save becomes enabled.
  const dirtyGateFailing = hasChanges === false;
  const saveDisabled = !formValid || submitting || dirtyGateFailing;
  const disabledTooltip = submitting
    ? "Saving…"
    : !formValid
      ? (validationReason ?? "Fill in the required fields to continue.")
      : dirtyGateFailing
        ? "No changes to save."
        : "";

  const submitButton = (
    <Tooltip title={saveDisabled ? disabledTooltip : ""} disableHoverListener={!saveDisabled}>
      {/* span wrapper so Tooltip can render over a disabled button (MUI
          strips pointer events from disabled buttons otherwise). */}
      <span>
        <Button
          type="submit"
          variant="contained"
          size="large"
          onClick={() => formik.handleSubmit()}
          disabled={saveDisabled}
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.2)" },
          }}
        >
          {submitting && <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />}
          {submitting ? "Saving…" : submitLabel}
        </Button>
      </span>
    </Tooltip>
  );

  // Compact Save for the top sticky bar — same handler, same disabled state,
  // same tooltip. The sticky bar is edit-mode only; Create keeps the
  // original single-bottom-action layout.
  const topSaveButton = (
    <Tooltip title={saveDisabled ? disabledTooltip : ""} disableHoverListener={!saveDisabled}>
      <span>
        <Button
          type="submit"
          variant="contained"
          size="small"
          onClick={() => formik.handleSubmit()}
          disabled={saveDisabled}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 3 }}
        >
          {submitting && <CircularProgress size={14} sx={{ mr: 1, color: "inherit" }} />}
          {submitting ? "Saving…" : "Save Changes"}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={3}>
          {/* ── Page Header + sticky top actions (edit only) ──
              Edit forms are long; scrolling to the bottom to find Save is
              friction. The top bar reuses the SAME submit path as the
              bottom Save (one `onClick={() => formik.handleSubmit()}` under
              a <form> that already has `onSubmit={formik.handleSubmit}`) —
              Formik dedupes concurrent submits, and the shared `submitting`
              gate stops both buttons during an in-flight request. Sticky
              positioning keeps the actions visible without a redesign;
              browsers that ignore `position: sticky` just fall back to
              normal flow. Create mode skips this bar — its single
              bottom Save is enough. */}
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
              ...(mode === "edit"
                ? {
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    bgcolor: "background.default",
                    py: 1.25,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }
                : {}),
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary" }}>
                {heading ?? defaultHeading}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {subheading ?? defaultSubheading}
              </Typography>
            </Box>
            {mode === "edit" && (
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                {showCancel !== false && onCancel && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={onCancel}
                    disabled={submitting}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Cancel
                  </Button>
                )}
                {topSaveButton}
              </Box>
            )}
          </Box>

          {contextBand}

          {/* ── Section 1: Staff & Class Setup ── */}
          <SectionCard>
            <SectionHeader>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              >
                <MdPerson size={20} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Staff & Class Details
              </Typography>
            </SectionHeader>

            <SectionBody>
              <Grid container spacing={2.5}>
                {/* Staff Search */}
                <Grid item xs={12} md={6}>
                  <CapsLabel required>Staff Member </CapsLabel>
                  <Autocomplete
                    disableClearable
                    disabled={!!staffLocked}
                    popupIcon={null}
                    forcePopupIcon={false}
                    options={filteredStaffOptions}
                    loading={studentPerentIsLoading}
                    getOptionLabel={(option: CustomerOption) => option.label}
                    value={
                      studentPerentOptions.find(
                        (opt) => opt.value === Number(formik.values.staff_id)
                      ) || undefined
                    }
                    onChange={onStaffChange}
                    onInputChange={(_, inputValue, reason) => {
                      if (reason === "input") onStaffSearchInput(inputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={staffLocked ? "" : "Search staff..."}
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <InputAdornment position="end">
                              {studentPerentIsLoading ? (
                                <CircularProgress size={18} />
                              ) : staffLocked ? (
                                <Chip
                                  size="small"
                                  label="Locked"
                                  sx={{
                                    height: 20,
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    bgcolor: alpha(theme.palette.text.disabled, 0.08),
                                    color: theme.palette.text.secondary,
                                  }}
                                />
                              ) : (
                                <IoSearchSharp
                                  style={{
                                    fontSize: "20px",
                                    color: theme.palette.text.disabled,
                                  }}
                                />
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    sx={{
                      width: "100%",
                      ...(staffLocked && {
                        "& .MuiInputBase-root.Mui-disabled": {
                          bgcolor: alpha(theme.palette.text.disabled, 0.04),
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: theme.palette.text.primary,
                          fontWeight: 600,
                        },
                      }),
                    }}
                  />
                </Grid>

                {/* Class name (optional curator override). When set, wins
                     over the pivot-based ClassLabel synthesis everywhere
                     — schedules, invoices, dashboards, parent portal.
                     Empty stays empty (backend model mutator coerces to
                     NULL). Live preview shows what the class WOULD
                     display as without a name, so the curator sees what
                     they are replacing. */}
                <Grid item xs={12} md={6}>
                  <CapsLabel>Class Name</CapsLabel>
                  <TextField
                    placeholder="e.g. Multi-Grade Room A"
                    size="small"
                    fullWidth
                    {...formik.getFieldProps("name")}
                    InputProps={{ sx: { fontSize: 14 } }}
                    helperText={(() => {
                      const nm = (formik.values.name ?? "").trim();
                      if (nm !== "") return "This name replaces the auto-generated label.";
                      const gradeNames: string[] = [];
                      const subjectNames: string[] = [];
                      for (const g of (subjectGRadeDataList ?? [])) {
                        for (const s of ((g as any).subjects ?? [])) {
                          if (selectedSubject.includes(s.id)) {
                            if (!gradeNames.includes((g as any).grade)) gradeNames.push((g as any).grade);
                            if (!subjectNames.includes(s.name || s.subject_name)) {
                              subjectNames.push(s.name || s.subject_name);
                            }
                          }
                        }
                      }
                      if (gradeNames.length === 0 && subjectNames.length === 0) {
                        return "Optional. Leave blank to auto-generate from grades and subjects.";
                      }
                      return `Will display as: ${previewCompactLabel(gradeNames, subjectNames)}`;
                    })()}
                  />
                </Grid>

                {/* Number of Students (with capacity floor hint in edit mode) */}
                <Grid item xs={12} md={6}>
                  <CapsLabel required>Number of Students </CapsLabel>
                  <TextField
                    placeholder="e.g. 5"
                    size="small"
                    {...formik.getFieldProps("number_of_student")}
                    type="number"
                    fullWidth
                    inputProps={
                      mode === "edit" && assignedCount != null
                        ? { min: assignedCount }
                        : undefined
                    }
                    error={
                      mode === "edit" &&
                      assignedCount != null &&
                      formik.values.number_of_student != null &&
                      Number(formik.values.number_of_student) < assignedCount
                    }
                    helperText={
                      mode === "edit" && assignedCount != null
                        ? formik.values.number_of_student != null &&
                          Number(formik.values.number_of_student) < assignedCount
                          ? `Capacity cannot be lower than the ${assignedCount} student(s) currently assigned.`
                          : `${assignedCount} currently assigned`
                        : undefined
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MdGroups size={18} color={theme.palette.text.disabled} />
                        </InputAdornment>
                      ),
                      sx: { fontSize: 14 },
                    }}
                  />
                </Grid>

                {/* Teaching Mode */}
                <Grid item xs={12} md={6}>
                  <CapsLabel required>Teaching Mode </CapsLabel>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {[TeachMood.Online, TeachMood.Offline].map((type) => {
                      const toggles = togglesFromMode(formik.values.class_mode);
                      const isSelected = type === TeachMood.Online ? toggles.online : toggles.inPerson;
                      return (
                        <ModeButton
                          key={type}
                          selected={isSelected}
                          onClick={() => {
                            const next =
                              type === TeachMood.Online
                                ? modeFromToggles(!toggles.online, toggles.inPerson)
                                : modeFromToggles(toggles.online, !toggles.inPerson);
                            void onClassModeChange(next);
                          }}
                          sx={{ flex: 1 }}
                        >
                          {type === TeachMood.Online ? <MdLaptop size={18} /> : <MdLocationOn size={18} />}
                          {studyMoodLabel(type)}
                        </ModeButton>
                      );
                    })}
                  </Box>
                </Grid>

                {/* Location */}
                <Grid item xs={12} md={6}>
                  <CapsLabel required={requiresLocation(formik.values.class_mode)}>Location </CapsLabel>
                  {!formik.values.class_mode ? (
                    <Box
                      sx={{
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.text.disabled, 0.06),
                      }}
                    >
                      <Typography sx={{ fontSize: 13, color: "text.disabled", fontStyle: "italic" }}>
                        Select teaching mode first
                      </Typography>
                    </Box>
                  ) : formik.values.class_mode === TeachMood.Online ? (
                    <Box
                      sx={{
                        height: 40,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        borderRadius: 1,
                        bgcolor: alpha("#6366f1", 0.06),
                        border: "1px solid",
                        borderColor: alpha("#6366f1", 0.15),
                      }}
                    >
                      <MdLaptop size={16} color="#6366f1" />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#6366f1" }}>
                        Online — no location needed
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Autocomplete
                        options={filteredLocationOptions}
                        loading={locationIsLoading}
                        getOptionLabel={(option: CustomerOption) => option.label}
                        value={
                          locationOptions.find(
                            (opt) => opt.value === Number(formik.values.location_id)
                          ) || null
                        }
                        onChange={(_, newValue: CustomerOption | null) => {
                          formik.setFieldValue("location_id", newValue?.value || "");
                        }}
                        noOptionsText={
                          staffLocationIds.length > 0
                            ? "No locations assigned to this staff"
                            : "No locations available"
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select location..."
                            size="small"
                            error={
                              isLocationInvalid ||
                              (requiresLocation(formik.values.class_mode) &&
                                !formik.values.location_id &&
                                formik.submitCount > 0)
                            }
                            helperText={
                              isLocationInvalid
                                ? "This staff is not assigned to the selected location"
                                : formik.values.class_mode === TeachMood.Offline &&
                                  !formik.values.location_id &&
                                  formik.submitCount > 0
                                  ? "Location is required for in-person classes"
                                  : ""
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {locationIsLoading ? <CircularProgress size={18} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        sx={{ width: "100%" }}
                      />
                      {formik.values.staff_id && staffLocationIds.length > 0 && !isLocationInvalid && (
                        <Typography
                          sx={{ fontSize: 11, color: "text.disabled", mt: 0.5, fontStyle: "italic" }}
                        >
                          Available locations based on selected staff
                        </Typography>
                      )}
                    </Box>
                  )}
                </Grid>

                {/* Class Type */}
                <Grid item xs={12} md={6}>
                  <CapsLabel required>Class Type </CapsLabel>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {Object.values(ClassType).map((type) => (
                      <ModeButton
                        key={type}
                        selected={formik.values.class_type === type}
                        onClick={() => onClassTypeChange(type)}
                        sx={{ flex: 1 }}
                      >
                        {type === "Regular" ? <MdSchedule size={18} /> : <MdCalendarToday size={18} />}
                        {type}
                      </ModeButton>
                    ))}
                  </Box>
                </Grid>

                {/* Program Type */}
                <Grid item xs={12} md={6}>
                  <CapsLabel>Program Type</CapsLabel>
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    {[
                      { key: "regular", label: "Regular" },
                      { key: "special", label: "Special" },
                    ].map((p) => (
                      <ModeButton
                        key={p.key}
                        selected={programType === p.key}
                        onClick={() => onProgramTypeChange(p.key as "regular" | "special")}
                        sx={{ flex: 1 }}
                      >
                        {p.label}
                      </ModeButton>
                    ))}
                  </Box>
                </Grid>

                {/* Period-Based Date Range */}
                {formik.values.class_type === ClassType["Period-Based"] && (
                  <Grid item xs={12} md={6}>
                    <CapsLabel>Date Range</CapsLabel>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={formik.values.period_based_from_date}
                          onChange={(date) => formik.setFieldValue("period_based_from_date", date)}
                          slotProps={{
                            textField: {
                              placeholder: "From",
                              size: "small",
                              sx: { flex: 1 },
                              error: Boolean(formik.errors.period_based_from_date),
                              helperText: formik.errors.period_based_from_date as string,
                              InputProps: { sx: { fontSize: 14 } },
                            },
                          }}
                        />
                      </LocalizationProvider>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={formik.values.period_based_to_date}
                          onChange={(date) => formik.setFieldValue("period_based_to_date", date)}
                          slotProps={{
                            textField: {
                              placeholder: "To",
                              size: "small",
                              sx: { flex: 1 },
                              error: Boolean(formik.errors.period_based_to_date),
                              helperText: formik.errors.period_based_to_date as string,
                              InputProps: { sx: { fontSize: 14 } },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </Grid>
                )}

                {/* Notes (Period-Based) */}
                {formik.values.class_type === ClassType["Period-Based"] && (
                  <Grid item xs={12}>
                    <CapsLabel>Memo</CapsLabel>
                    <TextField
                      placeholder="Add notes about this class..."
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      {...formik.getFieldProps("notes")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                            <MdNotes size={18} color={theme.palette.text.disabled} />
                          </InputAdornment>
                        ),
                        sx: { fontSize: 14 },
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </SectionBody>
          </SectionCard>

          {/* ── Section 2: Subjects & Grades ── */}
          <SectionCard>
            <SectionHeader>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha("#54b8e8", 0.1),
                  color: "#54b8e8",
                }}
              >
                <MdSubject size={20} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Subjects & Grades
              </Typography>
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                {selectedSubject.length > 0 && (
                  <Chip
                    label={`${selectedSubject.length} selected`}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {onAddSubjectAssignments && (
                  <Button
                    onClick={() => setAddSubjectOpen(true)}
                    size="small"
                    variant="outlined"
                    startIcon={<MdAdd size={16} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: alpha("#54b8e8", 0.5),
                      color: "#54b8e8",
                      "&:hover": {
                        borderColor: "#54b8e8",
                        bgcolor: alpha("#54b8e8", 0.06),
                      },
                    }}
                  >
                    Add Teaching Subject
                  </Button>
                )}
              </Box>
            </SectionHeader>

            <SectionBody>
              {subjectIsLoading || subjectGradeIsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, gap: 1.5 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Loading subjects...
                  </Typography>
                </Box>
              ) : isSubjectError || subjectGRadeDataList.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    px: 2,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                    border: "1px dashed",
                    borderColor: alpha(theme.palette.warning.main, 0.2),
                  }}
                >
                  <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                    {isSubjectError
                      ? "Error fetching data"
                      : "Grades and subjects have not been assigned to the selected teacher"}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {subjectGRadeDataList.map((item: IGrade) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                      }}
                    >
                      <GradeBadge>{item.grade}</GradeBadge>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1 }}>
                        {item.selected_sub_array
                          ?.filter((subject) => subject.is_assigned_to_staff)
                          .map((subItem: ISubjectGradePerent, index: number) => {
                            const isSelected = selectedSubject.includes(subItem.id);
                            const isProtected = !!protectedSubjectIds?.has(subItem.id);
                            const pill = (
                              <SubjectChip
                                key={index}
                                selected={isSelected}
                                locked={isProtected}
                                onClick={() => {
                                  // Protected subjects (have generated classes,
                                  // enrolled students, attendance or billing) stay
                                  // selected. The parent supplies the ID set; if
                                  // absent, nothing is locked.
                                  if (isProtected && isSelected) return;
                                  onSubjectToggle(subItem.id);
                                }}
                              >
                                {subjectMap.get(subItem.subject_id)}
                              </SubjectChip>
                            );
                            return isProtected && isSelected ? (
                              <Tooltip
                                key={index}
                                title={
                                  protectedSubjectMessage ||
                                  "This subject cannot be removed because classes or student assignments already exist for it."
                                }
                                arrow
                              >
                                <span>{pill}</span>
                              </Tooltip>
                            ) : (
                              pill
                            );
                          })}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </SectionBody>
          </SectionCard>

          {/* ── Section 3: Schedule ── */}
          <SectionCard>
            <SectionHeader>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha("#10b981", 0.1),
                  color: "#10b981",
                }}
              >
                <MdSchedule size={20} />
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Schedule
              </Typography>
            </SectionHeader>

            <SectionBody>
              <CapsLabel required>Days </CapsLabel>
              <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
                {Object.values(WeekDays).map((day) => {
                  // Case-insensitive comparison via normalizeWeekday so a
                  // persisted lowercase/short-form token still highlights
                  // the right button. Defence in depth — the Edit page's
                  // seed effect normalises on hydration, this guard covers
                  // any future caller that forgets to.
                  const isSelected = selectedWeekDays.some(
                    (d) => _normalizeWeekday(d) === day,
                  );
                  const dayConflictCount = timeConflicts.filter((c) => c.day === day).length;
                  const hasThisDayConflict = isSelected && dayConflictCount > 0;

                  const chip = (
                    <DayChip
                      key={day}
                      selected={isSelected}
                      onClick={() => onWeekDayToggle(day)}
                      sx={{
                        ...(hasThisDayConflict && {
                          border: "2px solid #ef4444",
                          bgcolor: isSelected ? "#ef4444" : alpha("#ef4444", 0.06),
                          color: isSelected ? "#fff" : "#ef4444",
                          position: "relative",
                          "&:hover": {
                            bgcolor: isSelected ? "#dc2626" : alpha("#ef4444", 0.1),
                          },
                        }),
                      }}
                    >
                      {day.slice(0, 3)}
                      {hasThisDayConflict && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: -4,
                            right: -4,
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            bgcolor: "#ef4444",
                            border: "2px solid #fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography sx={{ fontSize: 7, color: "#fff", fontWeight: 700 }}>!</Typography>
                        </Box>
                      )}
                    </DayChip>
                  );

                  return hasThisDayConflict ? (
                    <Tooltip
                      key={day}
                      title={`Conflict: Staff has ${dayConflictCount} overlapping class${dayConflictCount > 1 ? "es" : ""} on ${day}`}
                      arrow
                    >
                      {chip}
                    </Tooltip>
                  ) : (
                    chip
                  );
                })}
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <CapsLabel required>Start Time </CapsLabel>
                  <PremiumTimeInput
                    id="class-start-time"
                    value={formik.values.startTime}
                    onChange={(time) => formik.setFieldValue("startTime", time)}
                    open={activeTimePicker === "start"}
                    onOpen={() => setActiveTimePicker("start")}
                    onClose={() => setActiveTimePicker((v) => (v === "start" ? null : v))}
                    error={Boolean(formik.touched.startTime && formik.errors.startTime)}
                  />
                  {formik.touched.startTime && formik.errors.startTime && (
                    <Typography color="error" sx={{ fontSize: 12, mt: 0.5 }}>
                      {formik.errors.startTime as string}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CapsLabel required>End Time </CapsLabel>
                  <PremiumTimeInput
                    id="class-end-time"
                    value={formik.values.endTime}
                    onChange={(time) => formik.setFieldValue("endTime", time)}
                    open={activeTimePicker === "end"}
                    onOpen={() => setActiveTimePicker("end")}
                    onClose={() => setActiveTimePicker((v) => (v === "end" ? null : v))}
                    error={Boolean(formik.touched.endTime && formik.errors.endTime)}
                  />
                  {formik.touched.endTime && formik.errors.endTime && (
                    <Typography color="error" sx={{ fontSize: 12, mt: 0.5 }}>
                      {formik.errors.endTime as string}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {timeOrderError && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: "8px",
                    bgcolor: alpha("#ef4444", 0.05),
                    border: `1px solid ${alpha("#ef4444", 0.15)}`,
                  }}
                >
                  <WarningAmberIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#dc2626" }}>
                    {timeOrderError}
                  </Typography>
                </Box>
              )}

              {hasConflicts && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: alpha("#ef4444", 0.04),
                    border: `1.5px solid ${alpha("#ef4444", 0.2)}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <WarningAmberIcon sx={{ fontSize: 20, color: "#ef4444" }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>
                      Schedule Conflict Detected
                    </Typography>
                    <Chip
                      label={`${timeConflicts.length} overlap${timeConflicts.length > 1 ? "s" : ""}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 10.5,
                        fontWeight: 700,
                        bgcolor: alpha("#ef4444", 0.1),
                        color: "#dc2626",
                        ml: "auto",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#991b1b", mb: 1.5 }}>
                    This staff already has classes during the selected time. Resolve conflicts before {mode === "edit" ? "updating" : "creating"}.
                  </Typography>
                  <Stack spacing={1}>
                    {timeConflicts.slice(0, 2).map((c, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1.25,
                          borderRadius: "8px",
                          bgcolor: "#fff",
                          border: `1px solid ${alpha("#ef4444", 0.15)}`,
                        }}
                      >
                        <Box sx={{ width: 4, height: 32, borderRadius: 2, bgcolor: "#ef4444", flexShrink: 0 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#1f2937" }}>
                            {c.day} — {c.startTime} to {c.endTime}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                            Existing class overlaps with your selected time
                          </Typography>
                        </Box>
                        <WarningAmberIcon sx={{ fontSize: 16, color: "#f59e0b", flexShrink: 0 }} />
                      </Box>
                    ))}
                    {timeConflicts.length > 2 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 1,
                          borderRadius: "8px",
                          bgcolor: alpha("#ef4444", 0.04),
                          border: `1px dashed ${alpha("#ef4444", 0.2)}`,
                        }}
                      >
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#dc2626" }}>
                          +{timeConflicts.length - 2} more overlapping class{timeConflicts.length - 2 > 1 ? "es" : ""}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </SectionBody>
          </SectionCard>

          {/* ── Section: Billing & Fee (Phase B) ── */}
          <SectionCard>
            <SectionBody sx={{ py: 2 }}>
              <ClassBillingSection
                fee={formik.values.default_fee ?? null}
                taxStatus={formik.values.default_tax_status ?? "exempt"}
                effectiveFrom={formik.values.default_fee_effective_from ?? null}
                currentFeeFromServer={props.currentClassFeeFromServer ?? null}
                onChange={(patch) => {
                  if ("fee" in patch) formik.setFieldValue("default_fee", patch.fee);
                  if ("taxStatus" in patch) formik.setFieldValue("default_tax_status", patch.taxStatus);
                  if ("effectiveFrom" in patch) formik.setFieldValue("default_fee_effective_from", patch.effectiveFrom);
                }}
              />
            </SectionBody>
          </SectionCard>

          {/* ── Edit Scope band (Edit only, when recurring) ──
              Historically this was a tri-selector (This / Future / Series).
              The backend's updateClass path only implements the "this and
              following sessions" behaviour — it always regenerates future
              occurrences from today and always protects past + invoiced
              sessions, regardless of the scope value sent. The two extra
              options were misleading (clicking "This session only" silently
              modified every future session), so the picker was reduced to
              an informational band that describes what actually happens.
              If per-occurrence and past-touching series edits land server-
              side later, restore the choice buttons and route each branch
              on the backend. */}
          {showEditScopeBand && onEditScopeChange && (
            <SectionCard>
              <SectionBody sx={{ py: 2 }}>
                <CapsLabel>Apply changes to</CapsLabel>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                    border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    Changes apply to <b>this session and all future sessions</b>.
                    Past sessions and invoiced sessions are preserved.
                  </Typography>
                </Box>
              </SectionBody>
            </SectionCard>
          )}

          {/* ── Actions ── */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 1 }}>
            {showCancel && onCancel && (
              <Button
                variant="text"
                size="large"
                onClick={onCancel}
                sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600, px: 4 }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="outlined"
              size="large"
              onClick={onReset}
              disabled={submitting}
              sx={{ borderRadius: 3, textTransform: "none", fontWeight: 600, px: 4 }}
            >
              Reset
            </Button>
            {submitButton}
          </Box>
        </Stack>
      </form>

      {/* Add Teaching Subject dialog — appends new grade+subject pair-ids
          to the parent's selectedSubject state via onAddSubjectAssignments.
          The parent keeps every other form value untouched (staff, mode,
          location, class type, program, days, times, capacity, recurrence). */}
      {onAddSubjectAssignments && (
        <AddTeachingSubjectDialog
          open={addSubjectOpen}
          onClose={() => setAddSubjectOpen(false)}
          subjectGRadeDataList={subjectGRadeDataList}
          subjectMap={subjectMap}
          selectedGradeSubjectIds={selectedSubject}
          protectedIds={protectedSubjectIds}
          onAdd={(ids) => onAddSubjectAssignments(ids)}
        />
      )}
    </FormikProvider>
  );
};

export default ClassForm;
